import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, FileText, Loader2, Swords } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  buildMatterBattleMap,
  getMatter,
  getMatterBattleMap,
  listMatterDocuments,
  updateBattleAllegation,
  type AllegationStatus,
  type BattleAllegation,
  type BattleMap as BattleMapData,
  type DocumentSummary,
} from '@/lib/api-client';
import type { MatterDetail } from '@/lib/types';

// Pleadings/allegations battle map (Sprint P5 Section 3). The operator tags the
// statement of claim and their defence; Opus extracts every allegation and maps
// position + evidence each way + gaps. The map is editable and feeds the ask.
export function BattleMap() {
  const { id = '' } = useParams<{ id: string }>();
  const [matter, setMatter] = useState<MatterDetail | null>(null);
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [map, setMap] = useState<BattleMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [claimIds, setClaimIds] = useState<Set<string>>(new Set());
  const [defenceIds, setDefenceIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      getMatter(id).catch(() => null),
      listMatterDocuments(id).catch(() => []),
      getMatterBattleMap(id).catch(() => null),
    ]).then(([m, d, bm]) => {
      if (!active) return;
      setMatter(m);
      setDocs(d);
      setMap(bm);
      setShowBuilder(!bm);
      if (bm) {
        setClaimIds(new Set(bm.claimDocumentIds));
        setDefenceIds(new Set(bm.defenceDocumentIds));
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [id]);

  const readyDocs = useMemo(() => docs.filter((d) => d.extractionStatus === 'READY'), [docs]);

  function toggle(set: Set<string>, setter: (s: Set<string>) => void, docId: string): void {
    const next = new Set(set);
    if (next.has(docId)) next.delete(docId);
    else next.add(docId);
    setter(next);
  }

  async function build(): Promise<void> {
    setError(null);
    setBuilding(true);
    try {
      const { map: built } = await buildMatterBattleMap(id, [...claimIds], [...defenceIds]);
      setMap(built);
      setShowBuilder(false);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not build the battle map. Try again.',
      );
    } finally {
      setBuilding(false);
    }
  }

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-4xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-2 font-display text-3xl italic text-stone-100">
              <Swords className="h-6 w-6 text-amber-500" /> Battle map
            </h1>
            {matter ? <p className="text-sm text-stone-400">{matter.name}</p> : null}
          </div>
          <Link
            to="/matters"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Matters
          </Link>
        </div>

        {error ? (
          <Card variant="default" padding="md" className="flex items-start gap-2 border-red-900/40 bg-red-950/10">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
            <p className="text-sm text-red-300">{error}</p>
          </Card>
        ) : null}

        {loading ? (
          <Card variant="default" padding="lg" className="text-sm text-stone-500">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </Card>
        ) : building ? (
          <Card variant="default" padding="lg" className="text-sm text-stone-400">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
            Reading the pleadings and mapping every allegation against your evidence. This deep
            analysis can take one to two minutes. Leave the tab open.
          </Card>
        ) : showBuilder ? (
          <Builder
            readyDocs={readyDocs}
            claimIds={claimIds}
            defenceIds={defenceIds}
            onToggleClaim={(docId) => toggle(claimIds, setClaimIds, docId)}
            onToggleDefence={(docId) => toggle(defenceIds, setDefenceIds, docId)}
            onBuild={() => void build()}
            hasExisting={Boolean(map)}
            onCancel={map ? () => setShowBuilder(false) : undefined}
            matterId={id}
          />
        ) : map ? (
          <MapView map={map} matterId={id} onRebuild={() => setShowBuilder(true)} onChange={setMap} />
        ) : null}
      </div>
    </div>
  );
}

function Builder({
  readyDocs,
  claimIds,
  defenceIds,
  onToggleClaim,
  onToggleDefence,
  onBuild,
  hasExisting,
  onCancel,
  matterId,
}: {
  readyDocs: DocumentSummary[];
  claimIds: Set<string>;
  defenceIds: Set<string>;
  onToggleClaim: (id: string) => void;
  onToggleDefence: (id: string) => void;
  onBuild: () => void;
  hasExisting: boolean;
  onCancel?: () => void;
  matterId: string;
}) {
  const canBuild = claimIds.size > 0;
  return (
    <Card variant="panel" padding="lg" className="space-y-4">
      <div>
        <p className="text-sm text-stone-300">
          Tag which documents are the <span className="text-amber-400">statement of claim</span> (the
          allegations against you) and which are your <span className="text-amber-400">defence</span>.
          Court Copilot reads only what you tag and never invents an allegation.
        </p>
      </div>

      {readyDocs.length === 0 ? (
        <p className="text-sm text-stone-500">
          No ready documents.{' '}
          <Link to={`/admin/matters/${matterId}/documents`} className="text-amber-400 hover:underline">
            Upload the pleadings
          </Link>{' '}
          first, then come back.
        </p>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center gap-3 px-3 text-[10px] uppercase tracking-[0.2em] text-stone-600">
            <span className="flex-1">Document</span>
            <span className="w-12 text-center">Claim</span>
            <span className="w-12 text-center">Defence</span>
          </div>
          {readyDocs.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-3 rounded-md border border-stone-800/60 bg-stone-900/50 px-3 py-2"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-stone-500" />
              <span className="min-w-0 flex-1 truncate text-sm text-stone-300">{d.filename}</span>
              <label className="flex w-12 justify-center" title="Tag as statement of claim">
                <input
                  type="checkbox"
                  checked={claimIds.has(d.id)}
                  onChange={() => onToggleClaim(d.id)}
                  className="h-4 w-4 accent-amber-600"
                />
              </label>
              <label className="flex w-12 justify-center" title="Tag as defence">
                <input
                  type="checkbox"
                  checked={defenceIds.has(d.id)}
                  onChange={() => onToggleDefence(d.id)}
                  className="h-4 w-4 accent-amber-600"
                />
              </label>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBuild}
          disabled={!canBuild}
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
        >
          <Swords className="h-4 w-4" />
          {hasExisting ? 'Rebuild battle map' : 'Build battle map'}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-300"
          >
            Cancel
          </button>
        ) : null}
        {!canBuild ? (
          <span className="text-[11px] text-stone-600">Tag at least one claim document to build.</span>
        ) : null}
      </div>
    </Card>
  );
}

function MapView({
  map,
  matterId,
  onRebuild,
  onChange,
}: {
  map: BattleMapData;
  matterId: string;
  onRebuild: () => void;
  onChange: (m: BattleMapData) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-stone-500">
          {map.allegations.length} allegations · generated{' '}
          {new Date(map.generatedAt).toLocaleString()}
        </p>
        <button
          type="button"
          onClick={onRebuild}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-amber-500 hover:text-amber-400"
        >
          <Swords className="h-3.5 w-3.5" /> Rebuild
        </button>
      </div>
      {map.allegations.length === 0 ? (
        <Card variant="default" padding="lg" className="text-sm text-stone-400">
          No allegations were extracted. Check that the document you tagged as the statement of claim
          actually contains the pleaded allegations, then rebuild.
        </Card>
      ) : (
        map.allegations.map((a) => (
          <AllegationCard
            key={a.id}
            matterId={matterId}
            allegation={a}
            onSaved={(updated) =>
              onChange({
                ...map,
                allegations: map.allegations.map((x) => (x.id === updated.id ? updated : x)),
              })
            }
          />
        ))
      )}
    </div>
  );
}

const STATUS_STYLES: Record<AllegationStatus, string> = {
  DENIED: 'border-amber-700/50 bg-amber-950/20 text-amber-400',
  NOT_ADMITTED: 'border-sky-800/50 bg-sky-950/20 text-sky-300',
  ADMITTED: 'border-stone-700 bg-stone-800/40 text-stone-400',
  UNCLEAR: 'border-red-900/50 bg-red-950/20 text-red-300',
};

function AllegationCard({
  matterId,
  allegation,
  onSaved,
}: {
  matterId: string;
  allegation: BattleAllegation;
  onSaved: (a: BattleAllegation) => void;
}) {
  const [position, setPosition] = useState(allegation.anthonyPosition);
  const [notes, setNotes] = useState(allegation.notes);
  const [status, setStatus] = useState<AllegationStatus>(allegation.status);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const dirty =
    position !== allegation.anthonyPosition ||
    notes !== allegation.notes ||
    status !== allegation.status;

  async function save(): Promise<void> {
    setBusy(true);
    setSaved(false);
    try {
      const updatedMap = await updateBattleAllegation(matterId, allegation.id, {
        anthonyPosition: position,
        notes,
        status,
      });
      const updated = updatedMap?.allegations.find((x) => x.id === allegation.id);
      if (updated) {
        onSaved(updated);
        setSaved(true);
        window.setTimeout(() => setSaved(false), 2000);
      }
    } catch {
      // leave the edits in place so the operator can retry
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="panel" padding="md" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {allegation.paragraphRef ? (
            <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
              Para {allegation.paragraphRef}
            </span>
          ) : null}
          <p className="text-sm text-stone-200">{allegation.claimText}</p>
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as AllegationStatus)}
          className={`shrink-0 rounded border px-2 py-1 text-[10px] uppercase tracking-[0.15em] focus:outline-none ${STATUS_STYLES[status]}`}
        >
          <option value="DENIED">Denied</option>
          <option value="NOT_ADMITTED">Not admitted</option>
          <option value="ADMITTED">Admitted</option>
          <option value="UNCLEAR">Unclear</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <EvidenceList label="Evidence for me" items={allegation.supportingEvidence} tone="emerald" />
        <EvidenceList label="Evidence against me" items={allegation.contradictingEvidence} tone="red" />
      </div>

      {allegation.gaps ? (
        <p className="rounded-md border border-stone-800/60 bg-stone-900/40 px-3 py-2 text-xs text-stone-400">
          <span className="uppercase tracking-[0.15em] text-stone-600">Gap · </span>
          {allegation.gaps}
        </p>
      ) : null}

      <div>
        <label className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500">
          My position
        </label>
        <textarea
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 focus:border-amber-700/60 focus:outline-none"
        />
      </div>
      <div>
        <label className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 focus:border-amber-700/60 focus:outline-none"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || busy}
          className="inline-flex items-center gap-2 rounded-md border border-stone-700 px-3 py-1.5 text-sm text-stone-200 hover:border-amber-700/60 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save
        </button>
        {saved ? <span className="text-xs text-emerald-500">Saved</span> : null}
      </div>
    </Card>
  );
}

function EvidenceList({
  label,
  items,
  tone,
}: {
  label: string;
  items: string[];
  tone: 'emerald' | 'red';
}) {
  const dot = tone === 'emerald' ? 'text-emerald-500' : 'text-red-400';
  return (
    <div>
      <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</p>
      {items.length === 0 ? (
        <p className="text-xs text-stone-600">None found in the loaded material.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it, i) => (
            <li key={i} className="flex gap-1.5 text-xs text-stone-300">
              <span className={dot}>•</span>
              <span className="min-w-0">{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
